"use client";

import { useState } from "react";
import { useSingleFlight, type TransactionRecord } from "@/lib/transaction-flow";
import type { FlowModel } from "./shared";

/**
 * `input → review → confirm → durable result` — the legacy
 * `TransactionFlowDialog`'s state machine, verbatim: one request in flight
 * (a second click is skipped), a `null` outcome keeps the review open for the
 * wallet panel, a success may step aside (`closeOnSuccess`, read after the
 * confirm resolved), a failure always stops on its receipt, and a confirm
 * that throws is still written up as a stated failure.
 */
export function useFlowSteps(m: FlowModel) {
  const isFa = m.lang === "fa";
  const [step, setStep] = useState<"input" | "review" | "result">(m.hasInput ? "input" : "review");
  const [result, setResult] = useState<TransactionRecord | null>(null);
  const {
    run,
    pending
  } = useSingleFlight();
  const isContinueDisabled = m.disableContinue ?? Boolean(m.blockedReason);
  const handleConfirm = async () => {
    if (m.blockedReason) return;
    try {
      const outcome = await run(async requestId => m.onConfirm(requestId));
      // `skipped` means a second click arrived while the first was still in
      // flight. Exactly one request left this client; nothing else to do.
      if (outcome.skipped) return;
      // The attempt is mid-sequence (external wallet). Stay put: the panel
      // under the review is what the user has to act on next.
      if (!outcome.result) return;
      m.onRecord(outcome.result);
      const stepAside = typeof m.closeOnSuccess === "function" ? m.closeOnSuccess() : m.closeOnSuccess;
      if (stepAside && outcome.result.outcome === "success") {
        // The page behind is the acknowledgement.
        m.onClose();
        return;
      }
      setResult(outcome.result);
      setStep("result");
    } catch {
      // `onConfirm` is contracted to resolve with a failure record rather than
      // throw. This catch makes a contract violation a stated failure instead
      // of an unhandled rejection.
      const failure: TransactionRecord = {
        requestId: `local-${Date.now()}`,
        flow: "unknown",
        outcome: "failure",
        title: isFa ? "این تراکنش انجام نشد" : "This transaction did not go through",
        description: isFa ? "چیزی ارسال نشد. می‌توانید دوباره تلاش کنید." : "Nothing was submitted. You can try again.",
        at: new Date().toISOString()
      };
      m.onRecord(failure);
      setResult(failure);
      setStep("result");
    }
  };
  return {
    step,
    setStep,
    result,
    pending,
    isContinueDisabled,
    handleConfirm
  };
}